/**
 * Module 'fleet': agent versions, update rings and rollouts (architecture doc 6.2 agent.update,
 * 8.2 Machines, 11.3 minimum version).
 *
 *   release    an agent version a rollout can offer. The hub never builds or signs agent code
 *              (node-channel security design D14, ADR 0042 decision 6): releases come from
 *              offline-signed v1 release manifests (./release.ts) in the folder ORGUS_RELEASES_DIR
 *              names, with the binaries next to them. The hub checks each manifest against the
 *              same release keys the node carries and lists only the good ones; the node checks
 *              everything again against the keys built into its own binary. Without that folder
 *              a hub has no releases, and agents are updated by hand.
 *   offer      carries the signed manifest byte for byte and a one-machine download path for
 *              the binary of that machine's target. Never a downgrade: a rollout only offers a
 *              version above what a machine runs, and the node refuses anything below its running
 *              version or its floor whatever the hub says.
 *   rollout    one version, ring by ring: machines in the 'test' ring first, then, once an admin
 *              promotes it, the 'fleet'. A failure in the test ring halts it. A rollout the update
 *              policy starts is staged instead: a canary (the test ring and a few per cent), then
 *              25 %, then every machine, each stage after a soak, halted when too many fail.
 *   policy     updates are on by default (R-EN-1): when a newer verified release appears in the
 *              feed for a workspace's channel, the hub starts a staged rollout by itself, audited
 *              as 'policy'. Admins choose the channel (stable or beta, per workspace and per
 *              machine), automatic or manual, the stages and soaks, the halt rate, maintenance
 *              windows and freezes; anyone with operator rights can pause automatic updates.
 *   drain      lives in the scheduler module (Machines > Drain); the fleet module widens what a
 *              drained machine refuses to every action that starts work.
 *   manage     inventory, health and versions are console actions too (R-ND-2): each request
 *              answers a report and writes an audit entry ('fleet.inventory', 'fleet.health',
 *              'fleet.versions'), like updates (fleet), drain (scheduler) and revoke (the hub).
 *
 *   hub -> agent  ext fleet 'update.offer'    UpdateOffer      (a signed command frame, like every ext)
 *   agent -> hub  ext fleet 'status'          FleetAgentStatus (on every connect, and on change)
 *   agent -> hub  ext fleet 'update.progress' UpdateProgress
 *   agent -> hub  GET /api/fleet/download/<ticket>             the binary named in the offer
 */
import type { Id, Machine, Ts } from '../index.ts';
import { csvCell } from './audit-ops.ts';
import type { ReleaseFile, ReleaseRefusal, ReleaseTarget } from './release.ts';

export const FLEET_EXT = 'fleet';

/** The exit code an agent uses to ask its launcher for a restart into the version in current.json. */
export const AGENT_RESTART_EXIT_CODE = 75;
/** A machine that took an offer and said nothing for this long has failed the update. */
export const UPDATE_STALL_MS = 15 * 60_000;
/**
 * An agent that waits for running work before it restarts repeats 'waiting-for-work' this often,
 * so a long headless session never looks like a stall. While those reports are fresh the hub
 * refuses new work there, so the wait ends.
 */
export const UPDATE_WAIT_REPORT_MS = 60_000;

export type AgentRing = 'test' | 'fleet';
export const AGENT_RINGS: readonly AgentRing[] = ['test', 'fleet'];
export const RING_LABEL: Record<AgentRing, string> = { test: 'Test ring', fleet: 'Fleet' };

/** One agent version a rollout can offer, as the release feed names it. */
export interface AgentRelease {
  version: string;
  /** sha256 of the signed release document (the manifest.json the hub read), lowercase hex. */
  sha256: string;
  /** Bytes of that document. */
  size: number;
  /** When it was signed (the manifest's issuedAt). */
  createdAt: Ts;
  /** Who published it: the release key that signed it ("release key <keyId>"). */
  publishedBy: string;
  // From the signed manifest. Optional so a feed that predates signed releases still fits.
  commit?: string;
  /** Nodes below this version update first. */
  floor?: string;
  /** Versions no node may install or keep running. */
  revoked?: string[];
  /** The signed manifest stops being valid at this time; a fresh one must be signed before. */
  expires?: Ts;
  keyId?: string;
  /** Each target's published file, size and sha256. */
  targets?: Partial<Record<ReleaseTarget, ReleaseFile>>;
  /** Signed by a published TEST key: only a test build of the agent accepts it. */
  testKey?: boolean;
  /**
   * Which channel it was published to: where it sits in the feed (`beta/<version>/` is beta,
   * anything else stable). Not part of the signed manifest, so it decides only who is offered
   * the release first; every byte is still checked against the release keys.
   */
  channel?: UpdateChannel;
}

/** One manifest in the release folder that the hub refused, and why (shown in Machines). */
export interface ReleaseFeedProblem {
  /** The manifest's path inside the release folder, with forward slashes. */
  source: string;
  reason: ReleaseRefusal | 'unreadable' | 'files';
  detail: string;
  /** The version it names, when it could be read. */
  version?: string;
}

/** Where an agent fetches the binary an offer names. One machine, one release, one target. */
export const FLEET_DOWNLOAD_PREFIX = '/api/fleet/download/';
/** A download ticket works this long after the offer (as long as the hub waits for an answer). */
export const FLEET_DOWNLOAD_TTL_MS = 15 * 60_000;

// ---------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------

/**
 * ext fleet 'update.offer', hub -> agent. Signed as a command frame like every ext message. It
 * says "this version is due" and hands over what the hub mirrors; the agent checks every byte
 * itself, against keys it carries, never against anything else the hub sends. There is no
 * downgrade: the agent refuses a version below the one it runs or its floor.
 */
export interface UpdateOffer {
  rolloutId: Id;
  /** For display and progress; the agent trusts only `manifest`. */
  release: AgentRelease;
  /** The signed release document (SignedReleaseManifest JSON), exactly as published, byte for byte. */
  manifest: string;
  /** The target the hub picked for this machine (`<GOOS>-<GOARCH>`); the agent refuses one that is not its own. */
  target: ReleaseTarget;
  /** Hub path that serves this machine's binary: FLEET_DOWNLOAD_PREFIX + a ticket valid FLEET_DOWNLOAD_TTL_MS. */
  download: string;
  /** The channel the release was offered on (shown by the node's status; the node trusts only `manifest`). */
  channel?: UpdateChannel;
  /**
   * The node rolls the new version back when it is not welcomed by its server within this long
   * after it starts (UPDATE_HEALTH_TIMEOUT_MS when absent), not only when it crashes. Reported
   * as 'rolled-back' with `rollback: 'no-welcome'`.
   */
  healthTimeoutMs?: number;
}

/** Why an agent did not take an offer: the manifest checks of ./release.ts, then the node's own. */
export type UpdateRefusal =
  | ReleaseRefusal
  | 'stale'
  | 'revoked'
  | 'downgrade'
  | 'below-floor'
  | 'current'
  | 'rolled-back'
  | 'frozen'
  | 'wrong-target'
  | 'download'
  | 'wrong-size'
  | 'wrong-hash'
  | 'install'
  | 'not-updatable'
  /**
   * The node fetched the vendor's fixed-origin manifest (RELEASE_ORIGIN_PREFIX, channelManifestPath)
   * and it disagrees with the server's offer (an older or different release): refused, and the
   * server raises an alert.
   */
  | 'origin-mismatch';

export const UPDATE_REFUSALS: readonly UpdateRefusal[] = [
  'malformed',
  'unknown-key',
  'bad-signature',
  'expired',
  'stale',
  'revoked',
  'downgrade',
  'below-floor',
  'current',
  'rolled-back',
  'frozen',
  'wrong-target',
  'download',
  'wrong-size',
  'wrong-hash',
  'install',
  'not-updatable',
  'origin-mismatch',
];

export const UPDATE_REFUSAL_LABEL: Record<UpdateRefusal, string> = {
  malformed: 'The release manifest is not a valid v1 manifest',
  'unknown-key': 'The release manifest is signed by a key this agent does not trust',
  'bad-signature': 'The release manifest signature does not match its contents',
  expired: 'The release manifest has expired',
  stale: 'The release manifest is older than one this agent already accepted',
  revoked: 'That version is revoked',
  downgrade: 'That version is older than the one running',
  'below-floor': 'That version is below the highest version this agent has run',
  current: 'That version is already running',
  'rolled-back': 'That version was rolled back here before',
  frozen: 'An update freeze is on for this machine',
  'wrong-target': 'The offer is for another operating system or processor',
  download: 'The binary could not be downloaded',
  'wrong-size': 'The downloaded binary is not the size the manifest gives',
  'wrong-hash': 'The downloaded binary does not match the manifest',
  install: 'The new version could not be installed',
  'not-updatable': 'This agent cannot update itself',
  'origin-mismatch': 'The update server and the official release disagree',
};

export type UpdateStage = 'offered' | 'downloading' | 'verifying' | 'installing' | 'waiting-for-work' | 'restarting' | 'done' | 'failed' | 'rolled-back';

export const UPDATE_STAGE_LABEL: Record<UpdateStage, string> = {
  offered: 'Offered',
  downloading: 'Downloading',
  verifying: 'Checking the signature',
  installing: 'Installing',
  'waiting-for-work': 'Waiting for running work to finish',
  restarting: 'Restarting',
  done: 'Updated',
  failed: 'Update failed',
  'rolled-back': 'Rolled back',
};

/** ext fleet 'update.progress', agent -> hub. */
export interface UpdateProgress {
  rolloutId: Id;
  version: string;
  stage: UpdateStage;
  message?: string;
  /** With stage 'failed': which check refused the offer. */
  reason?: UpdateRefusal;
  /** With stage 'rolled-back': the new version crashed, or ran but was not welcomed in time. */
  rollback?: UpdateRollbackCause;
  at: Ts;
}

/** ext fleet 'status', agent -> hub, on every connect and when something changes. */
export interface FleetAgentStatus {
  version: string;
  /** True when this agent can replace itself with an offered version. */
  updatable: boolean;
  /** Why it cannot update itself (a source checkout, inside the hub process, no release feed). */
  why?: string;
  /** The version the launcher fell back from, when the last update did not come up. */
  rolledBackFrom?: string | null;
  /** How it is kept running: 'task-scheduler' | 'launchd' | 'systemd' | 'none' | 'source' | 'in-process'. */
  service: string;
  /** The enrolment that installed it, when one did. */
  enrolmentId?: string | null;
  /** The last update this agent worked on. */
  update?: UpdateProgress | null;
  /** Work that would be cut short by a restart. */
  busy: { jobs: number; runs: number };
  /** The release target this agent was built for (`<GOOS>-<GOARCH>`), when it knows it. */
  target?: ReleaseTarget;
  /** The highest version confirmed here: the agent installs nothing below it. */
  floor?: string | null;
  /** An update freeze on this machine holds offers until then. */
  frozenUntil?: Ts | null;
  /** The release keys built in: the owner's, the published TEST keys, or none yet. */
  keys?: 'release' | 'test' | 'none';
  /** The start entry that keeps it running, from service.json (a task, plist or unit name). */
  serviceEntry?: string | null;
  /** The channel it follows (from its pairing grant or its last offer). */
  channel?: UpdateChannel;
  /** A version started but not yet welcomed by the server: it rolls back after `healthTimeoutMs`. */
  pending?: { version: string; since: Ts } | null;
  at: Ts;
}

// ---------------------------------------------------------------------------
// Hub state: GET /api/fleet and state.ext.fleet
// ---------------------------------------------------------------------------

export type VersionDrift = 'current' | 'behind' | 'ahead' | 'unknown';

export interface FleetMachine {
  machineId: Id;
  ring: AgentRing;
  version: string;
  drift: VersionDrift;
  updatable: boolean | null;
  why: string | null;
  service: string | null;
  update: UpdateProgress | null;
  rolledBackFrom: string | null;
  busy: { jobs: number; runs: number } | null;
  statusAt: Ts | null;
  /** The release target the machine needs (reported by the agent, else from its OS and processor). */
  target?: ReleaseTarget | null;
  /** The machine's own floor: it installs nothing below this version. */
  floor?: string | null;
  /** The machine's update freeze holds offers until then. */
  frozenUntil?: Ts | null;
  /** The release keys the machine's agent carries. */
  keys?: 'release' | 'test' | 'none' | null;
  /** The channel it follows: its own setting, else the workspace's. */
  channel?: UpdateChannel;
  /** Its own channel setting; null follows the workspace. */
  channelOverride?: UpdateChannel | null;
  /** The start entry that keeps the agent running (service.json), when reported. */
  serviceEntry?: string | null;
  /** Why it is not updating to the newest release of its channel; null when it is current or updating. */
  hold?: UpdateHold | null;
}

export type RolloutStatus = 'running' | 'awaiting-promotion' | 'done' | 'halted' | 'cancelled';
export type RolloutTargetState = 'pending' | 'offered' | 'updating' | 'done' | 'failed' | 'skipped';

export interface RolloutTarget {
  machineId: Id;
  ring: AgentRing;
  from: string;
  state: RolloutTargetState;
  message?: string;
  at: Ts;
  /** In a staged rollout: the stage (index into the plan's stages) that offers it. */
  wave?: number;
}

/** A staged rollout's plan, copied from the policy when it starts so a later edit does not move it. */
export interface RolloutPlan {
  stages: RolloutStagePlan[];
  /** The stage being offered now. */
  wave: number;
  /** Every machine of the stage is done: the next stage starts at this time. */
  soakUntil: Ts | null;
  haltFailureRate: number;
  haltMinFailures: number;
  channel: UpdateChannel;
}

export interface Rollout {
  id: Id;
  version: string;
  startedBy: string;
  startedAt: Ts;
  /** The ring being updated now. */
  stage: AgentRing;
  status: RolloutStatus;
  targets: RolloutTarget[];
  endedAt?: Ts;
  /** Why it halted or was cancelled. */
  note?: string;
  /** Present on a staged rollout (the policy's): stages by percentage, each after a soak. */
  plan?: RolloutPlan;
}

export const ROLLOUT_STATUS_LABEL: Record<RolloutStatus, string> = {
  running: 'Rolling out',
  'awaiting-promotion': 'Test ring updated, waiting for promotion',
  done: 'Rolled out',
  halted: 'Halted',
  cancelled: 'Cancelled',
};

export interface FleetState {
  /** Where releases come from; null while this hub has no release feed (updates by hand). */
  feed: string | null;
  /** Which release keys the hub checks the feed with: the owner's, or the TEST keys (tests only). */
  feedKeys?: 'release' | 'test' | null;
  /** Manifests in the release folder the hub refused (bad signature, expired, binaries that do not match). */
  feedProblems?: ReleaseFeedProblem[];
  /** Good releases, newest first. */
  releases: AgentRelease[];
  /** The newest released version (what "current" means for drift). */
  latest: string | null;
  rollout: Rollout | null;
  history: Rollout[];
  machines: FleetMachine[];
  /** The update policy (updates are on by default). Absent from a hub older than the policy. */
  policy?: UpdatePolicy;
  /** What automatic updates will do next, or why they will not. */
  auto?: AutoUpdateState;
  /** Quiet notices about recent rollouts, newest first ("Updated to 0.9.1 on 12 machines"). */
  notices?: FleetNotice[];
  /**
   * Machines refused an offer as 'origin-mismatch' in the last 7 days: this server offered
   * something the vendor's fixed release origin does not (it lags, or its feed was changed).
   */
  originAlert?: OriginAlert | null;
}

export interface OriginAlert {
  /** The latest refusal. */
  at: Ts;
  /** The version this server offered. */
  version: string;
  machineIds: Id[];
  /** What the latest node said. */
  detail: string;
}

/** How long an origin-mismatch alert stays when nothing clears it. */
export const ORIGIN_ALERT_MS = 7 * 24 * 3_600_000;

export const FLEET_AUDIT_LABEL: Record<string, string> = {
  'fleet.rollout-start': 'Start agent rollout',
  'fleet.rollout-promote': 'Promote agent rollout',
  'fleet.rollout-cancel': 'Cancel agent rollout',
  'fleet.rollout-halt': 'Agent rollout halted',
  'fleet.update': 'Update agent',
  'fleet.ring': 'Change update ring',
  'fleet.channel': 'Change update channel',
  'fleet.policy': 'Change update policy',
  'fleet.pause': 'Pause automatic updates',
  'fleet.resume': 'Resume automatic updates',
  'fleet.origin-mismatch': 'Update server and official release disagree',
  'fleet.inventory': 'Export machine inventory',
  'fleet.health': 'Check machine health',
  'fleet.versions': 'Check agent versions',
};

// ---------------------------------------------------------------------------
// Central management actions (R-ND-2): inventory, health and versions, each audited
// ---------------------------------------------------------------------------

export type FleetHealthStatus = 'healthy' | 'attention' | 'down';
export const FLEET_HEALTH_LABEL: Record<FleetHealthStatus, string> = { healthy: 'Healthy', attention: 'Needs attention', down: 'Down' };

/** One check of a health report. */
export interface FleetHealthCheck {
  key: 'connection' | 'status' | 'version' | 'update' | 'drain' | 'service';
  label: string;
  state: 'ok' | 'warn' | 'fail';
  detail: string;
}

/** POST /api/fleet/machines/:id/health: one machine's health, as the server sees it now. */
export interface FleetHealthReport {
  machineId: Id;
  name: string;
  status: FleetHealthStatus;
  checks: FleetHealthCheck[];
  at: Ts;
}

/** What `machineHealth` reads: the machine, its fleet view and whether the scheduler drained it. */
export interface MachineHealthInput {
  machine: Pick<Machine, 'id' | 'name' | 'online' | 'lastSeen' | 'agentVersion'>;
  fleet: Pick<FleetMachine, 'version' | 'drift' | 'update' | 'rolledBackFrom' | 'statusAt' | 'updatable' | 'why' | 'service' | 'hold'> | null;
  /** The agent's pending version (started, not yet welcomed), when it reported one. */
  pending?: { version: string; since: Ts } | null;
  latest: string | null;
  drained: boolean;
  /** A machine that sent nothing for this long is stale (STALE_MS.machine). */
  staleMs: number;
  now: Ts;
}

/** Health of one machine: down when offline, needs attention when any check warns, else healthy. */
export function machineHealth(i: MachineHealthInput): Pick<FleetHealthReport, 'status' | 'checks'> {
  const { machine: m, fleet: f, now } = i;
  const checks: FleetHealthCheck[] = [];
  const quiet = now - m.lastSeen;
  checks.push(
    !m.online
      ? { key: 'connection', label: 'Connection', state: 'fail', detail: 'Offline: its agent is not connected.' }
      : quiet > i.staleMs
        ? { key: 'connection', label: 'Connection', state: 'warn', detail: `Connected, but silent for ${Math.round(quiet / 1000)} s.` }
        : { key: 'connection', label: 'Connection', state: 'ok', detail: 'Connected and reporting.' },
  );
  checks.push(
    f?.statusAt
      ? { key: 'status', label: 'Agent status', state: 'ok', detail: 'The agent reports its version and update state.' }
      : { key: 'status', label: 'Agent status', state: 'warn', detail: 'No update status from this agent: it is updated by hand.' },
  );
  const version = f?.version || m.agentVersion;
  checks.push(
    f?.drift === 'behind'
      ? { key: 'version', label: 'Version', state: 'warn', detail: `${version || 'Unknown'}, behind ${i.latest ?? 'the latest release'}.` }
      : f?.drift === 'unknown' || !f
        ? { key: 'version', label: 'Version', state: 'ok', detail: `${version || 'Unknown'}; no release to compare with.` }
        : { key: 'version', label: 'Version', state: 'ok', detail: `${version}, ${f.drift === 'ahead' ? 'ahead of' : 'the'} latest release.` },
  );
  const u = f?.update;
  if (i.pending) checks.push({ key: 'update', label: 'Update', state: 'warn', detail: `${i.pending.version} started and waits to be welcomed; it rolls back if not welcomed in time.` });
  else if (u?.stage === 'failed') checks.push({ key: 'update', label: 'Update', state: 'warn', detail: `Update to ${u.version} failed${u.message ? `: ${u.message}` : '.'}` });
  else if (u?.stage === 'rolled-back' || f?.rolledBackFrom) checks.push({ key: 'update', label: 'Update', state: 'warn', detail: `Rolled back from ${u?.stage === 'rolled-back' ? u.version : f?.rolledBackFrom}${u?.rollback === 'no-welcome' ? ': not welcomed in time' : u?.rollback === 'crash' ? ': it did not start' : ''}.` });
  else checks.push({ key: 'update', label: 'Update', state: 'ok', detail: u ? `Last update: ${UPDATE_STAGE_LABEL[u.stage].toLowerCase()} (${u.version}).` : 'No update problems.' });
  checks.push(i.drained ? { key: 'drain', label: 'Drain', state: 'warn', detail: 'Drained: it takes no new work.' } : { key: 'drain', label: 'Drain', state: 'ok', detail: 'Takes new work.' });
  if (f && f.updatable === false) checks.push({ key: 'service', label: 'Service', state: 'ok', detail: `Updates by hand: ${f.why ?? 'not under the Orgus supervisor'}.` });
  const status: FleetHealthStatus = !m.online ? 'down' : checks.some((c) => c.state !== 'ok') ? 'attention' : 'healthy';
  return { status, checks };
}

/** One machine in the inventory. */
export interface FleetInventoryRow {
  machineId: Id;
  name: string;
  hostname: string;
  os: string;
  arch: string;
  version: string;
  drift: VersionDrift;
  channel: UpdateChannel;
  ring: AgentRing;
  health: FleetHealthStatus;
  online: boolean;
  lastSeen: Ts;
  drained: boolean;
  updatable: boolean | null;
  service: string | null;
  labels: string[];
}

/** POST /api/fleet/inventory: every machine with its version, health and settings, now. */
export interface FleetInventory {
  at: Ts;
  latest: string | null;
  rows: FleetInventoryRow[];
}

export const INVENTORY_CSV_COLUMNS = ['machine_id', 'name', 'hostname', 'os', 'arch', 'agent_version', 'drift', 'channel', 'ring', 'health', 'online', 'last_seen', 'drained', 'updatable', 'service', 'labels'] as const;

/** The inventory as CSV (cells neutralised like the audit export's). */
export function inventoryCsv(inv: FleetInventory): string {
  const rows = inv.rows.map((r) =>
    [r.machineId, r.name, r.hostname, r.os, r.arch, r.version, r.drift, r.channel, r.ring, r.health, r.online, new Date(r.lastSeen).toISOString(), r.drained, r.updatable ?? '', r.service ?? '', r.labels.join(' ')].map(csvCell).join(','),
  );
  return `${INVENTORY_CSV_COLUMNS.join(',')}\r\n${rows.map((r) => `${r}\r\n`).join('')}`;
}

/** POST /api/fleet/versions: the feed read again, and how many machines run each version. */
export interface FleetVersionsReport {
  at: Ts;
  latest: string | null;
  feed: string | null;
  versions: { version: string; drift: VersionDrift; machines: number }[];
  behind: number;
}

/** The drift of `version` against the latest release. */
export function driftOf(version: string, latest: string | null, compare: (a: string, b: string) => number): VersionDrift {
  if (!version || !latest) return 'unknown';
  const d = compare(version, latest);
  return d === 0 ? 'current' : d < 0 ? 'behind' : 'ahead';
}

// ---------------------------------------------------------------------------
// Update policy: automatic, staged updates (R-EN-1, R-ND-2)
// ---------------------------------------------------------------------------

/** Which releases a machine follows: stable only, or beta (the newest of either). */
export type UpdateChannel = 'stable' | 'beta';
export const UPDATE_CHANNELS: readonly UpdateChannel[] = ['stable', 'beta'];
export const CHANNEL_LABEL: Record<UpdateChannel, string> = { stable: 'Stable', beta: 'Beta' };

/** 'auto': the hub rolls out each new release by itself (the default). 'manual': an admin starts each rollout. */
export type UpdateMode = 'auto' | 'manual';
export const UPDATE_MODE_LABEL: Record<UpdateMode, string> = { auto: 'Automatic', manual: 'Manual' };

/** One stage of a staged rollout. */
export interface RolloutStagePlan {
  /** Share of the rollout's machines updated once this stage is offered, 1 to 100. The last stage is 100. */
  percent: number;
  /** How long the stage's machines run the new version before the next stage starts. */
  soakMs: number;
}

/** Offers of automatic rollouts go out only inside a window, when any are set. Times in the policy's time zone. */
export interface MaintenanceWindow {
  /** Days the window opens on: 0 Sunday to 6 Saturday. */
  days: number[];
  /** `HH:MM`, 24-hour. */
  start: string;
  /** `HH:MM`; at or before `start`, the window runs past midnight into the next day. */
  end: string;
}

/** A period with no update offers at all, automatic or by hand (a release week, an audit). */
export interface UpdateFreeze {
  id: Id;
  from: Ts;
  until: Ts;
  reason: string;
  by: string;
}

/** Automatic updates paused by a person: no new automatic rollout, and a running one sends no offers. */
export interface UpdatePause {
  by: string;
  at: Ts;
  /** Resumes by itself at this time; null until someone resumes it. */
  until: Ts | null;
  reason?: string;
}

export interface UpdatePolicy {
  mode: UpdateMode;
  /** The workspace's channel; a machine may set its own. */
  channel: UpdateChannel;
  /** Two to five stages, percentages rising, the last 100. */
  stages: RolloutStagePlan[];
  /** Halt when the failed machines reach this share of the machines offered so far, 0 to 1. */
  haltFailureRate: number;
  /** ...and at least this many failed. */
  haltMinFailures: number;
  windows: MaintenanceWindow[];
  /** IANA time zone the windows are in. */
  timeZone: string;
  freezes: UpdateFreeze[];
  paused: UpdatePause | null;
  updatedAt?: Ts;
  updatedBy?: string;
}

const HOUR = 3_600_000;

/** On by default: stable channel, a 5 % canary that soaks an hour, then 25 % for four hours, then everyone. */
export const DEFAULT_UPDATE_POLICY: UpdatePolicy = {
  mode: 'auto',
  channel: 'stable',
  stages: [
    { percent: 5, soakMs: HOUR },
    { percent: 25, soakMs: 4 * HOUR },
    { percent: 100, soakMs: 0 },
  ],
  haltFailureRate: 0.1,
  haltMinFailures: 1,
  windows: [],
  timeZone: 'UTC',
  freezes: [],
  paused: null,
};

/** What PUT /api/fleet/policy takes: any of these, the rest stays. */
export type UpdatePolicyInput = Partial<Pick<UpdatePolicy, 'mode' | 'channel' | 'stages' | 'haltFailureRate' | 'haltMinFailures' | 'windows' | 'timeZone'>> & {
  freezes?: Array<Pick<UpdateFreeze, 'from' | 'until' | 'reason'> & { id?: Id }>;
};

export const MAX_SOAK_MS = 14 * 24 * HOUR;
const HHMM = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;

/** True when the runtime knows this IANA time zone. */
export function isTimeZone(tz: unknown): tz is string {
  if (typeof tz !== 'string' || !tz || tz.length > 64) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

const isRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

/** Why an update policy input is not acceptable, or null. Shared by the hub, the control plane and the console form. */
export function updatePolicyProblem(p: UpdatePolicyInput): string | null {
  if (p.mode !== undefined && p.mode !== 'auto' && p.mode !== 'manual') return "The mode is 'auto' or 'manual'.";
  if (p.channel !== undefined && !UPDATE_CHANNELS.includes(p.channel)) return "The channel is 'stable' or 'beta'.";
  if (p.stages !== undefined) {
    if (!Array.isArray(p.stages) || p.stages.length < 2 || p.stages.length > 5) return 'A staged rollout has two to five stages.';
    let last = 0;
    for (const [i, s] of p.stages.entries()) {
      if (!isRecord(s) || !Number.isInteger(s.percent) || (s.percent as number) < 1 || (s.percent as number) > 100) return `Stage ${i + 1}: the share is a whole percentage from 1 to 100.`;
      if ((s.percent as number) <= last) return `Stage ${i + 1}: each stage reaches more machines than the one before.`;
      if (!Number.isInteger(s.soakMs) || (s.soakMs as number) < 0 || (s.soakMs as number) > MAX_SOAK_MS) return `Stage ${i + 1}: the soak is from 0 to 14 days.`;
      last = s.percent as number;
    }
    if (last !== 100) return 'The last stage reaches every machine (100 %).';
  }
  if (p.haltFailureRate !== undefined && (typeof p.haltFailureRate !== 'number' || !(p.haltFailureRate > 0) || p.haltFailureRate > 1)) return 'The halt rate is above 0 and at most 100 %.';
  if (p.haltMinFailures !== undefined && (!Number.isInteger(p.haltMinFailures) || p.haltMinFailures < 1 || p.haltMinFailures > 1000)) return 'A rollout halts after 1 to 1,000 failures.';
  if (p.timeZone !== undefined && !isTimeZone(p.timeZone)) return `${String(p.timeZone)} is not a time zone this server knows.`;
  if (p.windows !== undefined) {
    if (!Array.isArray(p.windows) || p.windows.length > 14) return 'At most 14 maintenance windows.';
    for (const [i, w] of p.windows.entries()) {
      if (!isRecord(w) || !Array.isArray(w.days) || !w.days.length || w.days.some((d) => !Number.isInteger(d) || d < 0 || d > 6)) return `Window ${i + 1}: pick at least one day.`;
      if (typeof w.start !== 'string' || typeof w.end !== 'string' || !HHMM.test(w.start) || !HHMM.test(w.end)) return `Window ${i + 1}: times are HH:MM, 24-hour.`;
      if (w.start === w.end) return `Window ${i + 1}: it opens and closes at the same time.`;
    }
  }
  if (p.freezes !== undefined) {
    if (!Array.isArray(p.freezes) || p.freezes.length > 20) return 'At most 20 freezes.';
    for (const [i, f] of p.freezes.entries()) {
      if (!isRecord(f) || !Number.isFinite(f.from) || !Number.isFinite(f.until) || (f.until as number) <= (f.from as number)) return `Freeze ${i + 1}: it ends after it starts.`;
      if (typeof f.reason !== 'string' || !f.reason.trim() || f.reason.length > 200) return `Freeze ${i + 1}: say why, in at most 200 characters.`;
    }
  }
  return null;
}

/** The freeze in force at `now`, if any (the one that lasts longest). */
export function activeUpdateFreeze(freezes: readonly UpdateFreeze[], now: number): UpdateFreeze | null {
  let hit: UpdateFreeze | null = null;
  for (const f of freezes) if (f.from <= now && now < f.until && (!hit || f.until > hit.until)) hit = f;
  return hit;
}

/** The pause in force at `now`, if any. */
export function activeUpdatePause(p: Pick<UpdatePolicy, 'paused'>, now: number): UpdatePause | null {
  return p.paused && (p.paused.until === null || p.paused.until > now) ? p.paused : null;
}

/** Day of week (0 Sunday) and minute of day at `now` in a time zone. */
function zonedClock(now: number, timeZone: string): { day: number; minute: number } {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date(now));
  const get = (t: string) => parts.find((x) => x.type === t)?.value ?? '';
  const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(get('weekday'));
  return { day: day < 0 ? 0 : day, minute: (Number(get('hour')) % 24) * 60 + Number(get('minute')) };
}

const minuteOf = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));

/** True when no window is set, or `now` is inside one. */
export function inMaintenanceWindow(windows: readonly MaintenanceWindow[], timeZone: string, now: number): boolean {
  if (!windows.length) return true;
  const { day, minute } = zonedClock(now, isTimeZone(timeZone) ? timeZone : 'UTC');
  for (const w of windows) {
    const s = minuteOf(w.start);
    const e = minuteOf(w.end);
    if (s < e) {
      if (w.days.includes(day) && minute >= s && minute < e) return true;
    } else {
      // Past midnight: the evening part on its own day, the morning part on the next.
      if (w.days.includes(day) && minute >= s) return true;
      if (w.days.includes((day + 6) % 7) && minute < e) return true;
    }
  }
  return false;
}

/** When the next window opens after `now` (to the quarter hour, within eight days), or null with no windows. */
export function nextWindowStart(windows: readonly MaintenanceWindow[], timeZone: string, now: number): Ts | null {
  if (!windows.length) return null;
  const step = 60_000;
  const start = Math.floor(now / step) * step;
  let was = inMaintenanceWindow(windows, timeZone, start);
  for (let t = start + step; t <= start + 8 * 24 * HOUR; t += step) {
    const is = inMaintenanceWindow(windows, timeZone, t);
    if (is && !was) return t;
    was = is;
  }
  return null;
}

/** Does a machine on `channel` take a release published to `release`? Beta takes both. */
export function channelTakes(channel: UpdateChannel, release: UpdateChannel | undefined): boolean {
  return channel === 'beta' || (release ?? 'stable') === 'stable';
}

/** "Canary, 5 %", "25 %" or "All machines", for a stage of a plan. */
export function stageName(stages: readonly RolloutStagePlan[], i: number): string {
  const s = stages[i];
  if (!s) return '';
  if (i === stages.length - 1) return 'All machines';
  return i === 0 ? `Canary, ${s.percent} %` : `${s.percent} %`;
}

/** Why a machine is not updating to the newest release of its channel. */
export type UpdateHoldReason =
  | 'no-feed'
  | 'not-reported'
  | 'not-updatable'
  | 'keys'
  | 'no-build'
  | 'rolled-back'
  | 'failed'
  | 'machine-freeze'
  | 'freeze'
  | 'manual'
  | 'paused'
  | 'window'
  | 'offline'
  | 'stage'
  | 'halted'
  | 'origin-mismatch'
  | 'queued';

export interface UpdateHold {
  reason: UpdateHoldReason;
  /** One sentence: what holds it and what would release it. */
  detail: string;
  /** When the hold ends by itself, when it does. */
  until?: Ts | null;
}

export const UPDATE_HOLD_LABEL: Record<UpdateHoldReason, string> = {
  'no-feed': 'No release feed',
  'not-reported': 'Not reported',
  'not-updatable': 'Updates by hand',
  keys: 'Different release keys',
  'no-build': 'No build for it',
  'rolled-back': 'Rolled back here',
  failed: 'Failed here',
  'machine-freeze': 'Frozen on the machine',
  freeze: 'Update freeze',
  manual: 'Automatic updates off',
  paused: 'Updates paused',
  window: 'Outside the window',
  offline: 'Offline',
  stage: 'Later stage',
  halted: 'Rollout halted',
  'origin-mismatch': 'Server and official release disagree',
  queued: 'Up next',
};

/** What automatic updates do next. */
export interface AutoUpdateState {
  /** Automatic updates are on and not paused. */
  on: boolean;
  /** The release they roll out next (or now), when there is one. */
  next: string | null;
  /** Why nothing moves now, when nothing does. */
  hold: UpdateHold | null;
}

export type FleetNoticeTone = 'success' | 'attention' | 'danger' | 'neutral';

/** A quiet line about a recent rollout. Derived from the rollout history, never stored on its own. */
export interface FleetNotice {
  id: string;
  at: Ts;
  tone: FleetNoticeTone;
  title: string;
  detail?: string;
  version: string;
  rolloutId: Id;
}

/** How long notices stay. */
export const FLEET_NOTICE_MS = 14 * 24 * HOUR;

// ---------------------------------------------------------------------------
// What the node client honours (agents/node-agent; R-EN-1, ADR 0042 D14 and D15)
// ---------------------------------------------------------------------------

/**
 * A version the supervisor started as pending and the server has not welcomed within this long
 * is rolled back, as a crash is (UpdateOffer.healthTimeoutMs overrides it). The node reports
 * `update.progress` stage 'rolled-back' with `rollback: 'no-welcome'`.
 */
export const UPDATE_HEALTH_TIMEOUT_MS = 10 * 60_000;

/** Why a node rolled a new version back. */
export type UpdateRollbackCause = 'crash' | 'no-welcome';

/** The socket close code for a revoked machine (hub and control plane). */
export const AGENT_REVOKED_CLOSE_CODE = 4403;
/**
 * A revoked or unauthorized node stops reconnecting hard: it forgets its enrolment, records
 * "removed by the server", and retries at most this often (or not at all once a person removes
 * Orgus), never the 1 s to 5 min crash backoff.
 */
export const AGENT_REVOKED_RETRY_MS = 6 * HOUR;

/** `<install root>/service.json`, written by `install` so the fleet status can name the start entry. */
export const AGENT_SERVICE_FILE = 'service.json';
export interface AgentServiceFile {
  /** 'task-scheduler' | 'run-key' | 'launchd' | 'systemd' (FleetAgentStatus.service). */
  kind: string;
  /** The task, plist label or unit name (FleetAgentStatus.serviceEntry). */
  name: string;
  installedAt: Ts;
  /** The version that wrote it. */
  version: string;
}

// ---------------------------------------------------------------------------
// The hosted release origin (WP-04; ADR 0042 decision 6)
// ---------------------------------------------------------------------------

/**
 * The fixed release origin, `https://downloads.<domain>`, served by the control plane from its
 * RELEASES bucket. It mirrors exactly what the owner signed offline; nothing there is signed by
 * a server, and only manifests that verify under the release keys, and the files they name, are
 * served. A node may fetch its binary here as well as from its server, and compares the server's
 * offer with the channel manifest here (a server that lags raises an alert).
 *
 *   GET /releases/<version>/manifest.json         a stable release's signed manifest, as published
 *   GET /releases/<version>/<file>                one of its binaries
 *   GET /releases/beta/<version>/manifest.json    the same for a beta release (and its files)
 *   GET /channels/<channel>/manifest.json         the newest verified release of the channel
 *
 * The same paths work under RELEASE_ORIGIN_PREFIX on the console origin (local runs, one-domain
 * setups). Bucket keys are the release paths without the leading slash.
 */
export const RELEASE_ORIGIN_PREFIX = '/downloads';
export const releaseFilePath = (version: string, file: string, channel: UpdateChannel = 'stable'): string => `/releases/${channel === 'beta' ? 'beta/' : ''}${version}/${file}`;
export const releaseManifestPath = (version: string, channel: UpdateChannel = 'stable'): string => releaseFilePath(version, 'manifest.json', channel);
export const channelManifestPath = (channel: UpdateChannel): string => `/channels/${channel}/manifest.json`;

// ---------------------------------------------------------------------------
// One-line installers (ADR 0100): the agent installed the way Claude Code is
// ---------------------------------------------------------------------------

/**
 * The fixed HTTPS origin the one-line installers come from and download from. The scripts carry
 * it built in (agents/node-agent/scripts/install/); they never take a hub address as their source.
 */
export const INSTALL_ORIGIN = 'https://downloads.orgus.ai';

/** Where the release origin serves the installers (at the root of downloads.<domain>, under /downloads elsewhere). */
export const INSTALL_SCRIPT_PATHS = { sh: '/install.sh', powershell: '/install.ps1', cmd: '/install.cmd' } as const;
/** The short text page that lists the three commands. */
export const INSTALL_PAGE_PATH = '/install';

/** The shell a one-line install command runs in: sh on macOS, Linux and WSL; Windows PowerShell; Windows CMD. */
export type InstallShell = keyof typeof INSTALL_SCRIPT_PATHS;

export const INSTALL_SHELL_LABEL: Record<InstallShell, string> = { sh: 'macOS, Linux, WSL', powershell: 'Windows PowerShell', cmd: 'Windows CMD' };

const SAFE_INSTALL_ARG = /^[A-Za-z0-9._:/=@+-]{1,512}$/;

/**
 * The one command that installs the agent from INSTALL_ORIGIN, with `args` passed through to
 * `orgus-node-agent install --pair` (for example ['--hub', url]). Arguments are limited to
 * characters no shell interprets, so the command means the same in all three; anything else throws.
 */
export function installOneLiner(shell: InstallShell, args: readonly string[] = []): string {
  for (const a of args) if (!SAFE_INSTALL_ARG.test(a)) throw new Error(`install argument ${JSON.stringify(a)} holds characters a shell would interpret`);
  const url = `${INSTALL_ORIGIN}${INSTALL_SCRIPT_PATHS[shell]}`;
  const rest = args.join(' ');
  if (shell === 'sh') return rest ? `curl -fsSL ${url} | sh -s -- ${rest}` : `curl -fsSL ${url} | sh`;
  if (shell === 'powershell') return rest ? `& ([scriptblock]::Create((irm ${url}))) ${rest}` : `irm ${url} | iex`;
  return `curl -fsSL ${url} -o install.cmd && install.cmd${rest ? ` ${rest}` : ''} && del install.cmd`;
}

// ---------------------------------------------------------------------------
// Downloads: what Add machine offers (WP-03, ADR 0042 decision 1 "download, run, approve")
// ---------------------------------------------------------------------------

/**
 * Signed-in routes that let a person download the node client for the computer they are adding:
 *
 *   GET /api/fleet/downloads            AgentDownloads (always 200; `unavailable` says why there is none)
 *   GET /api/fleet/downloads/:target    the binary the newest signed release names for that target,
 *                                       or 404 { error } with code 'no-download'
 *
 * The hub mirrors the binary the owner signed, byte for byte, from the release folder the fleet
 * feed reads (ORGUS_RELEASES_DIR); it is checked against the signed manifest before it is listed
 * and again on every download. Never a script, never a per-download tag: everyone gets the same
 * file (ADR 0042 decision 1), and the node pairs by asking a person to approve it.
 */
export const FLEET_DOWNLOADS_PATH = '/api/fleet/downloads';

/** How each release target reads in a download button. */
export const RELEASE_TARGET_LABEL: Record<ReleaseTarget, string> = {
  'windows-amd64': 'Windows (x64)',
  'windows-arm64': 'Windows (Arm64)',
  'darwin-arm64': 'macOS (Apple silicon)',
  'darwin-amd64': 'macOS (Intel)',
  'linux-amd64': 'Linux (x64)',
  'linux-arm64': 'Linux (Arm64)',
};

/** One downloadable file of the newest signed release. */
export interface AgentDownload {
  target: ReleaseTarget;
  /** The name it downloads as (from the signed manifest). */
  file: string;
  size: number;
  sha256: string;
  /** Same-origin path: FLEET_DOWNLOADS_PATH + '/' + target. */
  url: string;
}

/** GET /api/fleet/downloads. */
export interface AgentDownloads {
  /** The newest signed release, or null when there is none to download. */
  version: string | null;
  /** Who publishes the client, shown as "Signed by <publisher>". */
  publisher: string;
  /** The key that signed the release manifest; `test` when it is a published TEST key (never for real machines). */
  signature: { keyId: string; test: boolean } | null;
  /** The server the client connects to: its domain (no scheme, no port) and whether other computers can reach it. */
  server: { domain: string; reachable: boolean; why?: string };
  /** One per target the release has, in RELEASE_TARGETS order. */
  downloads: AgentDownload[];
  /** Why there is nothing to download, and what to do next. Null when `downloads` is not empty. */
  unavailable: { what: string; why: string; next: string; code: 'no-release-feed' | 'no-release' } | null;
  /**
   * A local hub (ADR 0042: "local hub"): the signed release carries only hosted Orgus's address, so
   * a downloaded agent opened here installs without pairing. The person runs
   * `orgus-node-agent install --pair --hub <url>` on the new computer instead, and approves it
   * (the browser it opens, or the three words it shows with --words). `url` is the address that
   * computer pairs with: https, or http to the hub's own computer. Absent: the download pairs by
   * itself when opened (hosted Orgus).
   */
  pairWith?: { url: string };
}
